/**
 * Feature Dependence Plot Component
 * 
 * Scatter plot showing relationship between feature value and SHAP value.
 * Helps identify thresholds where features become risky.
 * 
 * Following BRAIN-DESIGN-SYSTEM.md:
 * - Uses Recharts ScatterChart
 * - Color coding based on SHAP value (green=positive, red=negative)
 * - Proper grid and axis styling
 */

import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, ZAxis } from 'recharts';

interface DependencePlotProps {
  featureName: string;
  dataPoints: Array<{
    featureValue: number;
    shapValue: number;
  }>;
}

export const DependencePlot: React.FC<DependencePlotProps> = ({
  featureName,
  dataPoints
}) => {
  const { chartData, stats } = useMemo(() => {
    const data = dataPoints.map(point => ({
      x: point.featureValue,
      y: point.shapValue,
      z: Math.abs(point.shapValue) * 100
    }));

    const avgShap = dataPoints.length > 0
      ? dataPoints.reduce((sum, p) => sum + p.shapValue, 0) / dataPoints.length
      : 0;
    const maxShap = dataPoints.length > 0
      ? Math.max(...dataPoints.map(p => p.shapValue))
      : 0;
    const minShap = dataPoints.length > 0
      ? Math.min(...dataPoints.map(p => p.shapValue))
      : 0;

    return {
      chartData: data,
      stats: { avgShap, maxShap, minShap }
    };
  }, [dataPoints]);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom sx={{ color: 'text.primary' }}>
        Feature Dependence: {featureName}
      </Typography>
      
      <Box sx={{ display: 'flex', gap: 3, mb: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
        <Box>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            Data Points
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 600 }}>
            {dataPoints.length}
          </Typography>
        </Box>
        <Box>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            Avg SHAP
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 600 }}>
            {stats.avgShap.toFixed(3)}
          </Typography>
        </Box>
        <Box>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            Range
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 600 }}>
            [{stats.minShap.toFixed(2)}, {stats.maxShap.toFixed(2)}]
          </Typography>
        </Box>
      </Box>

      <ResponsiveContainer width="100%" height={400}>
        <ScatterChart margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
          <XAxis 
            type="number" 
            dataKey="x" 
            name="Feature Value"
            tick={{ fill: '#9CA3AF' }}
            label={{ value: featureName, position: 'insideBottom', offset: -10, fill: '#9CA3AF' }}
          />
          <YAxis 
            type="number" 
            dataKey="y" 
            name="SHAP Value"
            tick={{ fill: '#9CA3AF' }}
            label={{ value: 'SHAP Value', angle: -90, position: 'insideLeft', fill: '#9CA3AF' }}
          />
          <ZAxis type="number" dataKey="z" range={[50, 400]} />
          <Tooltip 
            cursor={{ strokeDasharray: '3 3' }}
            contentStyle={{ 
              backgroundColor: '#1F2937', 
              border: '1px solid #374151',
              borderRadius: '4px'
            }}
            labelStyle={{ color: '#F8FAFC' }}
            itemStyle={{ color: '#F8FAFC' }}
            formatter={(value: any, name: string) => {
              if (name === 'Feature Value') return [value.toFixed(3), name];
              if (name === 'SHAP Value') return [value.toFixed(3), name];
              return [value, name];
            }}
          />
          <ReferenceLine y={0} stroke="#6B7280" strokeDasharray="3 3" />
          <Scatter 
            name="Data Points" 
            data={chartData} 
            fill="#3B82F6"
            fillOpacity={0.6}
          />
        </ScatterChart>
      </ResponsiveContainer>

      <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(59, 130, 246, 0.1)', borderRadius: 1, border: '1px solid rgba(59, 130, 246, 0.2)' }}>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          <strong>Insight:</strong> This plot shows how the feature value affects the model&apos;s prediction. 
          Points above the zero line increase the prediction, while points below decrease it.
        </Typography>
      </Box>
    </Box>
  );
};

export default DependencePlot;
